import type { MutableRefObject } from "react";
import { useEffect, useRef } from "react";

export interface ComboboxProps<T> {
  items: T[];
  onSelect: (item: T) => void;
}

type ComboboxReturnValue = {
  inputRef: MutableRefObject<HTMLInputElement>;
  optionsRef: MutableRefObject<HTMLDivElement>;
};

export function useCombobox<T extends object>({ items, onSelect }: ComboboxProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const input = inputRef.current;
    const options = optionsRef.current;

    if (!input || !options) return;

    const listItems = Array.from(options.querySelectorAll("li"));

    const findActiveOption = (el: HTMLLIElement) => el.getAttribute("aria-selected") === "true";

    const setItemFocus = (index: number) => {
      const item = listItems[index];
      const activeOption = listItems.find(findActiveOption);

      if (activeOption) {
        activeOption.tabIndex = -1;
        activeOption.setAttribute("aria-selected", "false");
      }

      if (item) {
        item.tabIndex = 0;
        item.setAttribute("aria-selected", "true");
        item.scrollIntoView({ block: "nearest" });

        input.setAttribute("aria-activedescendant", item.getAttribute("id")!);
      }
    };

    const handleKeyDown = (ev: KeyboardEvent) => {
      const activeOptionIndex = listItems.findIndex(findActiveOption);

      switch (ev.key) {
        case "ArrowUp": {
          ev.preventDefault();

          const nextIndex = activeOptionIndex - 1;
          setItemFocus(nextIndex < 0 ? listItems.length - 1 : nextIndex);

          break;
        }
        case "ArrowDown": {
          ev.preventDefault();

          const nextIndex = activeOptionIndex + 1;
          setItemFocus(nextIndex % listItems.length);

          break;
        }
        case "Escape": {
          input.focus();
          break;
        }
        case "Home":
        case "PageUp": {
          setItemFocus(0);
          break;
        }
        case "End":
        case "PageDown": {
          setItemFocus(listItems.length - 1);
          break;
        }
        case "Enter":
        case "Tab": {
          ev.preventDefault();

          const activeOption = listItems[activeOptionIndex];

          if (activeOption) {
            const itemId = activeOption.getAttribute("data-value");

            const item = items.find((item) => {
              if ("id" in item) return item.id === itemId;
              return false;
            });

            if (item) onSelect(item);
          }
          break;
        }
        default: {
          if (activeOptionIndex === -1) setItemFocus(0);
          break;
        }
      }
    };

    input.addEventListener("keydown", handleKeyDown);
    options.addEventListener("keydown", handleKeyDown);

    input.focus();
    options.scrollTop = 0;

    return () => {
      input.removeEventListener("keydown", handleKeyDown);
      options.removeEventListener("keydown", handleKeyDown);
    };
  }, [items, onSelect]);

  return { inputRef, optionsRef } as ComboboxReturnValue;
}
